/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Users, ShoppingCart, CreditCard, Receipt, TrendingUp, TrendingDown,
  AlertTriangle, Trash2, Download, Loader2, Package, Globe, Building2, Wallet, Store,
} from 'lucide-react';

const CUR_SYM = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
const money = (n, c) => `${(Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${CUR_SYM[c] || (c || '').toUpperCase()}`;

// Distinct segment colours: emerald scale for Stripe accounts, indigo scale for
// Viva — so a store that rotated across several Stripe accounts stays readable.
const STRIPE_SHADES = ['#059669', '#10b981', '#34d399', '#047857', '#6ee7b7', '#065f46'];
const VIVA_SHADES = ['#4f46e5', '#6366f1', '#818cf8', '#4338ca', '#a5b4fc', '#3730a3'];
const acctColor = (psp, i) => (psp === 'viva' ? VIVA_SHADES : STRIPE_SHADES)[i % 6];
const pspBadge = (psp) => `inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-semibold border ${
  psp === 'viva'
    ? 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20'
}`;

// ---------- Revenue split by Stripe/Viva account (accounting) ----------
const StoreRevenueCard = ({ st }) => {
  const accounts = st.accounts || [];
  const numericSum = accounts.reduce((s, a) => s + (a.ca || 0), 0) || 1;
  const curTotals = {};
  (st.totals || []).forEach((t) => { curTotals[t.currency] = t.ca; });
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
      className="bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800 rounded-md p-4 flex flex-col gap-3"
      data-testid={`store-revenue-card-${st.store_id}`}
    >
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Store className="h-4 w-4 text-zinc-400 shrink-0" />
          <span className="font-display text-base tracking-tight font-semibold truncate">{st.store_name}</span>
          <span className="text-[10px] text-zinc-400 font-mono shrink-0">{accounts.length} compte{accounts.length > 1 ? 's' : ''}</span>
        </div>
        <div className="font-mono text-lg font-semibold whitespace-nowrap tabular-nums">
          {(st.totals || []).map((t) => money(t.ca, t.currency)).join(' · ')}
        </div>
      </div>

      {/* Segmented proportion bar (Stripe vs Viva, per account) */}
      <div
        className="w-full h-2 rounded-full flex overflow-hidden bg-zinc-200 dark:bg-zinc-800"
        role="img"
        aria-label={accounts.map((a) => `${a.account_name} ${Math.round((a.ca / numericSum) * 100)}%`).join(', ')}
      >
        {accounts.map((a, i) => (
          <motion.div
            key={`${a.key}-${a.currency}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(1, (a.ca / numericSum) * 100)}%` }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.15 }}
            style={{ backgroundColor: acctColor(a.psp, i) }}
            title={`${a.account_name} · ${money(a.ca, a.currency)}`}
          />
        ))}
      </div>

      {/* Account rows */}
      <div className="flex flex-col">
        {accounts.map((a, i) => {
          const denom = curTotals[a.currency] || numericSum;
          const share = denom > 0 ? (a.ca / denom) * 100 : 0;
          return (
            <div
              key={`${a.key}-${a.currency}`}
              className="group flex items-center justify-between gap-3 py-2.5 border-t border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100/60 dark:hover:bg-zinc-800/50 transition-colors -mx-4 px-4 rounded-sm cursor-default"
              data-testid={`rev-acct-${a.key}`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-transparent group-hover:ring-zinc-200 dark:group-hover:ring-zinc-700 transition-all" style={{ backgroundColor: acctColor(a.psp, i) }} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-800 dark:text-zinc-100 truncate">{a.account_name}</span>
                    <span className={pspBadge(a.psp)}>{a.psp}</span>
                  </div>
                  {a.vitrine_name && (
                    <div className="text-[10px] text-zinc-400 truncate flex items-center gap-1 mt-0.5">
                      <CreditCard className="h-3 w-3 shrink-0" /> {a.vitrine_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right whitespace-nowrap shrink-0">
                <div className="font-mono text-sm font-semibold tabular-nums">{money(a.ca, a.currency)}</div>
                <div className="text-[10px] text-zinc-400 font-mono tabular-nums">{share.toFixed(0)}% · {a.orders} cmd</div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

const RevenueByAccount = ({ revenue }) => {
  const stores = revenue?.stores || [];
  const grand = revenue?.grand_totals || [];
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <Card className="card-flat p-4 sm:p-6 mb-6" data-testid="revenue-by-account">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 pb-4 border-b border-zinc-200 dark:border-zinc-800">
          <div>
            <div className="overline mb-1 flex items-center gap-2"><Building2 className="h-3.5 w-3.5" /> — Répartition du CA par compte Stripe</div>
            <p className="text-xs text-zinc-500 max-w-md">
              CA encaissé (payé) par compte sur la période — idéal pour la compta quand la Batmobile fait tourner les comptes.
            </p>
          </div>
          {grand.length > 0 && (
            <div className="text-left sm:text-right" data-testid="revenue-grand-total">
              <div className="overline mb-1">Total encaissé</div>
              <div className="font-mono text-2xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums" data-testid="rev-grand-total">
                {grand.map((t) => money(t.ca, t.currency)).join(' · ')}
              </div>
            </div>
          )}
        </div>
        {stores.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-400 gap-3 border border-dashed border-zinc-300 dark:border-zinc-700 rounded-md mt-4">
            <Wallet className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
            <div className="text-sm">Aucun encaissement sur la période.</div>
          </div>
        ) : (
          <motion.div
            className="flex flex-col gap-4 mt-4"
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
            initial="hidden"
            animate="show"
          >
            {stores.map((st) => <StoreRevenueCard key={st.store_id} st={st} />)}
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
};

const RANGES = [
  { value: 7, label: '7 derniers jours' },
  { value: 30, label: '30 derniers jours' },
  { value: 90, label: '90 derniers jours' },
  { value: 365, label: '12 derniers mois' },
];
const GLOBAL = '__global__';

// Build an ISO [00:00, +24h) range for a given YYYY-MM-DD (browser-local).
function dayRange(yyyymmdd) {
  const start = new Date(`${yyyymmdd}T00:00:00`);
  const end = new Date(start.getTime() + 24 * 3600 * 1000);
  return { since: start.toISOString(), until: end.toISOString() };
}
const dateStr = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dd}`;
};
const todayStr = () => dateStr(new Date());
const yesterdayStr = () => dateStr(new Date(Date.now() - 86400000));

// ---------- Delta / number formatters ----------
const Delta = ({ value, inverse }) => {
  // inverse=true → red when going up (e.g. failure rate). Default: green up.
  if (value === 0 || value === null || value === undefined) {
    return <span className="text-[10px] text-zinc-400 font-mono">·</span>;
  }
  const up = value > 0;
  const good = inverse ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono ${good ? 'text-emerald-600' : 'text-red-600'}`}>
      <Icon className="h-3 w-3" /> {value > 0 ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
};

const Tile = ({ icon: Icon, label, value, suffix, delta, deltaInverse, accent }) => (
  <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 text-[10px] text-zinc-500 uppercase tracking-widest">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="mt-2 font-display text-2xl lg:text-3xl font-bold tracking-tight" style={accent ? { color: accent } : {}}>
      {value}{suffix && <span className="text-base text-zinc-400 font-normal ml-1">{suffix}</span>}
    </div>
    {delta !== undefined && <div className="mt-1"><Delta value={delta} inverse={deltaInverse} /></div>}
  </div>
);

const FunnelStep = ({ step, max }) => {
  const w = max ? Math.max(2, (step.unique_sessions / max) * 100) : 2;
  return (
    <div className="grid grid-cols-[160px_1fr_auto] items-center gap-3 text-xs">
      <div className="text-zinc-600">{step.label}</div>
      <div className="h-3 rounded-full bg-zinc-100 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-zinc-700 transition-all" style={{ width: `${w}%` }} />
      </div>
      <div className="font-mono text-zinc-700 text-right whitespace-nowrap">
        <span className="font-semibold">{step.unique_sessions}</span>
        {step.conv_from_prev > 0 && (
          <span className="text-[10px] text-zinc-400 ml-1.5">→ {step.conv_from_prev.toFixed(0)}%</span>
        )}
      </div>
    </div>
  );
};

export default function Analytics() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(GLOBAL);
  const [days, setDays] = useState(30);
  // When set (YYYY-MM-DD), analytics are scoped to that single day instead of a rolling window.
  const [specificDay, setSpecificDay] = useState('');
  const [data, setData] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    api.get('/stores').then((r) => setStores(r.data));
  }, []);

  const fetchData = useMemo(() => async () => {
    setLoading(true);
    try {
      const base = storeId === GLOBAL ? '/analytics/overview' : `/stores/${storeId}/analytics/overview`;
      let path;
      if (specificDay) {
        const { since, until } = dayRange(specificDay);
        path = `${base}?since=${encodeURIComponent(since)}&until=${encodeURIComponent(until)}`;
      } else {
        path = `${base}?days=${days}`;
      }
      const r = await api.get(path);
      setData(r.data);
      // Accounting split: paid revenue by Stripe/Viva account, same period.
      try {
        const rp = new URLSearchParams();
        if (storeId !== GLOBAL) rp.set('store_id', storeId);
        if (specificDay) {
          const { since, until } = dayRange(specificDay);
          rp.set('since', since); rp.set('until', until);
        } else {
          rp.set('days', String(days));
        }
        const rr = await api.get(`/analytics/revenue-by-account?${rp.toString()}`);
        setRevenue(rr.data);
      } catch { setRevenue(null); }
    } catch (e) {
      toast.error('Impossible de charger les analytics');
      setData(null);
      setRevenue(null);
    } finally {
      setLoading(false);
    }
  }, [storeId, days, specificDay]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (storeId !== GLOBAL) params.set('store_id', storeId);
    if (data?.since) params.set('since', data.since);
    if (data?.until) params.set('until', data.until);
    params.set('status', 'paid');
    const headers = {};
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/export/transactions.csv?${params}`, { headers });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${storeId === GLOBAL ? 'global' : storeId.slice(0, 8)}_${days}j.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error('Échec de l\'export CSV');
    }
  };

  const totals = data?.funnel?.totals || {};
  const delta = data?.comparison?.delta_pct || {};
  const steps = data?.funnel?.steps || [];
  const maxStep = Math.max(1, ...steps.map((s) => s.unique_sessions || 0));
  const topProducts = data?.top_products || [];
  const sources = data?.traffic_sources || { by_channel: [], by_campaign: [], by_referer: [] };
  const failures = data?.payment_failures || [];
  const timeline = data?.timeline || [];
  const maxTimelineBar = Math.max(1, ...timeline.flatMap((d) => [d.visitors, d.checkout_views, d.succeeded]));

  const currentStore = stores.find((s) => s.id === storeId);
  const accent = currentStore?.accent_color || '#0A0A0A';

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 sm:mb-8 gap-4">
        <div>
          <div className="overline mb-2">— Pilotage</div>
          <h1 className="font-display text-2xl sm:text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Analytics</h1>
          <p className="text-zinc-600 mt-2 text-sm">
            Funnel détaillé · sources · top produits · échecs · comparaison période précédente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="analytics-export-csv" className="rounded-md flex-1 sm:flex-none">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => setResetOpen(true)}
            data-testid="analytics-open-reset"
            className="rounded-md text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Reset
          </Button>
        </div>
      </div>

      <Card className="card-flat p-4 sm:p-6 mb-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="overline mb-2 block">— Scope</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger data-testid="analytics-store-select" className="h-10 rounded-md">
                <SelectValue placeholder="Choisir un scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={GLOBAL}>
                  <span className="inline-flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> Toutes boutiques</span>
                </SelectItem>
                {stores.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline mb-2 block">— Période</Label>
            <Select value={String(days)} onValueChange={(v) => { setSpecificDay(''); setDays(parseInt(v, 10)); }} disabled={!!specificDay}>
              <SelectTrigger data-testid="analytics-range-select" className="h-10 rounded-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Daily mode: pick a single day (vs the previous day automatically). */}
        <div className="mt-4 pt-4 border-t border-zinc-100">
          <Label className="overline mb-2 block">— Vue par jour</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm" variant={specificDay === todayStr() ? 'default' : 'outline'}
              onClick={() => setSpecificDay(todayStr())}
              data-testid="analytics-day-today"
              className="rounded-md h-9"
            >
              Aujourd'hui
            </Button>
            <Button
              size="sm" variant={specificDay === yesterdayStr() ? 'default' : 'outline'}
              onClick={() => setSpecificDay(yesterdayStr())}
              data-testid="analytics-day-yesterday"
              className="rounded-md h-9"
            >
              Hier
            </Button>
            <Input
              type="date"
              value={specificDay}
              max={todayStr()}
              onChange={(e) => setSpecificDay(e.target.value)}
              data-testid="analytics-day-picker"
              className="h-9 rounded-md w-auto font-mono text-sm"
            />
            {specificDay && (
              <Button
                size="sm" variant="ghost"
                onClick={() => setSpecificDay('')}
                data-testid="analytics-day-clear"
                className="rounded-md h-9 text-zinc-500"
              >
                ✕ Revenir à la période
              </Button>
            )}
            {specificDay && (
              <span className="text-xs text-zinc-500 font-mono ml-1" data-testid="analytics-day-active">
                Jour : {specificDay} (comparé à la veille)
              </span>
            )}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* KPI tiles with comparison vs previous period */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
            <Tile icon={Users} label="Visiteurs" value={totals.visitors || 0} delta={delta.visitors} accent={accent} />
            <Tile icon={Package} label="Ajouts panier" value={totals.add_to_carts || 0} delta={delta.add_to_carts} accent={accent} />
            <Tile icon={ShoppingCart} label="Checkout" value={totals.checkout_views || 0} delta={delta.checkout_views} accent={accent} />
            <Tile icon={CreditCard} label="Réussis" value={totals.succeeded || 0} delta={delta.succeeded} accent={accent} />
            <Tile icon={AlertTriangle} label="Échecs" value={totals.failed || 0} delta={delta.failed} deltaInverse />
            <Tile icon={Receipt} label="Revenus" value={(totals.revenue || 0).toFixed(2)} suffix="€" delta={delta.revenue} accent={accent} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
            <Tile icon={TrendingUp} label="Conv. visiteurs" value={(totals.conv_visitors || 0).toFixed(1)} suffix="%" />
            <Tile icon={TrendingUp} label="Panier → checkout" value={(totals.conv_cart_to_checkout || 0).toFixed(1)} suffix="%" />
            <Tile icon={TrendingUp} label="Conv. checkout" value={(totals.conv_checkout || 0).toFixed(1)} suffix="%" />
            <Tile icon={TrendingUp} label="Taux réussite" value={(totals.success_rate || 0).toFixed(1)} suffix="%" delta={delta.success_rate} />
            <Tile icon={Receipt} label="Panier moyen" value={(totals.aov || 0).toFixed(2)} suffix="€" delta={delta.aov} />
          </div>

          {/* Revenue split by Stripe/Viva account (accounting) */}
          <RevenueByAccount revenue={revenue} />

          {/* Funnel */}
          <Card className="card-flat p-6 mb-6">
            <div className="overline mb-4">— Funnel détaillé</div>
            <div className="space-y-2.5" data-testid="analytics-funnel">
              {steps.map((s) => <FunnelStep key={s.type} step={s} max={maxStep} />)}
            </div>
            <div className="mt-4 pt-4 border-t border-zinc-100 text-[11px] text-zinc-500">
              Les pourcentages indiquent le taux de passage à l'étape précédente non-vide.
            </div>
          </Card>

          {/* Top products + Sources side by side */}
          <div className="grid lg:grid-cols-2 gap-3 mb-6">
            <Card className="card-flat p-6">
              <div className="overline mb-4 flex items-center gap-2"><Package className="h-3.5 w-3.5" /> — Top produits</div>
              {topProducts.length === 0 ? (
                <div className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-md p-6 text-center">Aucune vente sur la période.</div>
              ) : (
                <div className="space-y-2.5" data-testid="analytics-top-products">
                  {topProducts.map((p, i) => (
                    <div key={p.product_id} className="grid grid-cols-[24px_1fr_auto] items-center gap-3 text-sm">
                      <div className="text-zinc-400 font-mono text-xs">{String(i + 1).padStart(2, '0')}</div>
                      <div className="truncate">
                        <div className="font-semibold text-zinc-800 truncate">{p.name}</div>
                        <div className="text-[10px] text-zinc-400">{p.orders} cmd · {p.qty} unités</div>
                      </div>
                      <div className="font-mono font-semibold text-sm whitespace-nowrap">{p.revenue.toFixed(2)} €</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="card-flat p-6">
              <div className="overline mb-4 flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> — Sources de trafic</div>
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">Par canal</div>
                  {(sources.by_channel || []).length === 0 ? (
                    <div className="text-xs text-zinc-400">—</div>
                  ) : (
                    sources.by_channel.map((c) => (
                      <div key={c.channel} className="flex justify-between text-xs py-0.5">
                        <span className="font-mono text-zinc-700">{c.channel}</span>
                        <span className="font-mono text-zinc-500">{c.sessions}</span>
                      </div>
                    ))
                  )}
                </div>
                {(sources.by_campaign || []).length > 0 && (
                  <div>
                    <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">Campagnes UTM</div>
                    {sources.by_campaign.slice(0, 8).map((c) => (
                      <div key={`${c.utm_source}-${c.utm_medium}-${c.utm_campaign}`} className="flex justify-between text-xs py-0.5 gap-2">
                        <span className="truncate text-zinc-700">{c.utm_source}/{c.utm_medium}/{c.utm_campaign}</span>
                        <span className="font-mono text-zinc-500 shrink-0">{c.count}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(sources.by_referer || []).length > 0 && (
                  <div>
                    <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">Referer</div>
                    {sources.by_referer.slice(0, 6).map((r) => (
                      <div key={r.host} className="flex justify-between text-xs py-0.5">
                        <span className="font-mono text-zinc-700 truncate">{r.host}</span>
                        <span className="font-mono text-zinc-500 shrink-0">{r.sessions}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Failures */}
          {failures.length > 0 && (
            <Card className="card-flat p-6 mb-6 border-amber-200 bg-amber-50/40">
              <div className="overline mb-4 flex items-center gap-2 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> — Échecs de paiement</div>
              <div className="space-y-2 text-xs" data-testid="analytics-failures">
                {failures.map((f) => (
                  <div key={`${f.code}-${f.message}`} className="flex justify-between items-start gap-3 py-1">
                    <div className="min-w-0">
                      <div className="font-mono font-semibold text-zinc-700">{f.code}</div>
                      <div className="text-zinc-500 truncate">{f.message || '—'}</div>
                    </div>
                    <div className="font-mono text-zinc-700 shrink-0">{f.count}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Daily timeline */}
          <Card className="card-flat p-6">
            <div className="overline mb-4">— Évolution journalière</div>
            {timeline.length === 0 ? (
              <div className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-md p-6 text-center">Pas encore d'événements sur la période.</div>
            ) : (
              <div className="space-y-1.5" data-testid="analytics-timeline">
                {timeline.map((d) => (
                  <div key={d.day} className="grid grid-cols-[64px_1fr_auto] items-center gap-3 text-xs">
                    <div className="font-mono text-zinc-500 text-[10px]">{d.day.slice(5)}</div>
                    <div className="space-y-0.5">
                      <div className="h-1.5 rounded-full bg-zinc-100 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-zinc-300" style={{ width: `${(d.visitors / maxTimelineBar) * 100}%` }} />
                      </div>
                      <div className="h-1.5 rounded-full bg-zinc-100 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-zinc-500" style={{ width: `${(d.checkout_views / maxTimelineBar) * 100}%` }} />
                      </div>
                      <div className="h-1.5 rounded-full bg-zinc-100 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-emerald-500" style={{ width: `${(d.succeeded / maxTimelineBar) * 100}%` }} />
                      </div>
                    </div>
                    <div className="font-mono text-zinc-500 text-[10px] text-right whitespace-nowrap">
                      {d.visitors}·{d.checkout_views}·{d.succeeded} {d.failed ? <span className="text-red-500">·{d.failed}✗</span> : null}
                    </div>
                  </div>
                ))}
                <div className="flex gap-3 text-[10px] text-zinc-400 mt-3 pt-3 border-t border-zinc-100">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-zinc-300" /> visiteurs</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-zinc-500" /> checkout</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> achats</span>
                  <span className="inline-flex items-center gap-1 text-red-500">· échecs</span>
                </div>
              </div>
            )}
          </Card>
        </>
      ) : (
        <div className="text-center text-zinc-400 py-12 text-sm">Pas de données.</div>
      )}

      <ResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        stores={stores}
        defaultStoreId={storeId === GLOBAL ? '' : storeId}
        onDone={fetchData}
      />
    </div>
  );
}

// ---------- Reset dialog ----------
const ResetDialog = ({ open, onClose, stores, defaultStoreId, onDone }) => {
  const [scope, setScope] = useState('store');     // 'store' | 'global'
  const [storeId, setStoreId] = useState('');
  const [usePeriod, setUsePeriod] = useState(false);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [confirm, setConfirm] = useState('');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (open) {
      setScope(defaultStoreId ? 'store' : 'global');
      setStoreId(defaultStoreId || (stores[0]?.id || ''));
      setUsePeriod(false);
      setSince('');
      setUntil('');
      setConfirm('');
    }
  }, [open, defaultStoreId, stores]);

  const run = async () => {
    if (confirm !== 'RESET') {
      toast.error('Tapez RESET pour confirmer');
      return;
    }
    setRunning(true);
    try {
      const r = await api.post('/analytics/reset', {
        store_id: scope === 'store' ? storeId : null,
        since: usePeriod && since ? new Date(since).toISOString() : null,
        until: usePeriod && until ? new Date(until).toISOString() : null,
        confirm: 'RESET',
      });
      toast.success(`${r.data.deleted} événement(s) supprimé(s)`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur reset');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" /> Réinitialiser les events analytiques
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2 text-xs text-red-900">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              Cette action <strong>supprime définitivement</strong> les events (visiteurs, vues checkout, paiements). Les <strong>transactions Stripe restent intactes</strong>. Idéal pour repartir d'une base propre avant une nouvelle campagne.
            </div>
          </div>
          <div>
            <Label className="text-xs">SCOPE</Label>
            <div className="mt-1.5 space-y-2">
              <label className="flex items-start gap-2 p-3 border border-zinc-200 rounded-md cursor-pointer hover:bg-zinc-50" data-testid="reset-scope-store">
                <input type="radio" checked={scope === 'store'} onChange={() => setScope('store')} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Une boutique</div>
                  {scope === 'store' && (
                    <Select value={storeId} onValueChange={setStoreId}>
                      <SelectTrigger data-testid="reset-store-select" className="mt-2 h-9 rounded-md text-xs">
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </label>
              <label className="flex items-start gap-2 p-3 border border-zinc-200 rounded-md cursor-pointer hover:bg-zinc-50" data-testid="reset-scope-global">
                <input type="radio" checked={scope === 'global'} onChange={() => setScope('global')} className="mt-1" />
                <div>
                  <div className="text-sm font-semibold">Toutes les boutiques (global)</div>
                  <div className="text-xs text-zinc-500">Vide l'historique events de tout le compte.</div>
                </div>
              </label>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="reset-toggle-period">
            <input type="checkbox" checked={usePeriod} onChange={(e) => setUsePeriod(e.target.checked)} />
            <span>Limiter à une période (sinon : tout l'historique)</span>
          </label>
          {usePeriod && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">DEPUIS</Label>
                <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="mt-1 h-9 rounded-md text-xs" data-testid="reset-since" />
              </div>
              <div>
                <Label className="text-xs">JUSQU'À</Label>
                <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1 h-9 rounded-md text-xs" data-testid="reset-until" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">CONFIRMATION — tapez <span className="font-mono">RESET</span></Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" className="mt-1 h-9 rounded-md font-mono" data-testid="reset-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={running} className="rounded-md">Annuler</Button>
          <Button
            onClick={run}
            disabled={running || confirm !== 'RESET' || (scope === 'store' && !storeId)}
            data-testid="reset-confirm-button"
            className="rounded-md bg-red-600 hover:bg-red-700 text-white"
          >
            {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Effacement…</> : <><Trash2 className="h-4 w-4 mr-2" /> Effacer définitivement</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
